// src/components/OrganizationSettingsPage.js

/**
 * Organization Settings Page
 *
 * Allows admins to manage organization settings including:
 * - Organization logo upload/delete
 * - Organization tagline
 * - Organization name display
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faImage, faSpinner, faTrashAlt, faUpload,
  faSave, faArrowLeft, faCheckCircle, faExclamationTriangle, faBell,
  faPencilAlt, faTimes, faClock, faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Alert } from 'react-bootstrap';

import { db, functions, storage } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';

import './OrganizationSettingsPage.css';

// Cloud Function references
const uploadOrgLogoCallable = httpsCallable(functions, 'uploadOrgLogo');
const deleteOrgLogoCallable = httpsCallable(functions, 'deleteOrgLogo');
const updateOrgSettingsCallable = httpsCallable(functions, 'updateOrgSettings');
const getOrgSettingsCallable = httpsCallable(functions, 'getOrgSettings');

// Common US timezones with friendly names
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (AZ)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time (AST)' },
];

// US States for dropdown
const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "District Of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
  "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
  "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah",
  "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

// Constants
const MAX_LOGO_SIZE_MB = 5;
const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

function OrganizationSettingsPage() {
  const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();

  // State
  const [settings, setSettings] = useState({ name: '', tagline: '', logo: null });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [tagline, setTagline] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [commSettings, setCommSettings] = useState({
    dailyCapPerMember: 3,
    quietHoursStart: '21:00',
    quietHoursEnd: '08:00'
  });
  const [savingComm, setSavingComm] = useState(false);

  // Org details state
  const [orgData, setOrgData] = useState(null);
  const [editingOrg, setEditingOrg] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [orgEditData, setOrgEditData] = useState({
    orgName: '', orgAddress: '', orgCity: '', orgState: '', orgZip: ''
  });
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [timezoneSuccess, setTimezoneSuccess] = useState(false);

  const fileInputRef = useRef(null);

  // Fetch current settings on mount
  const fetchSettings = useCallback(async () => {
    if (!userOrgId) return;

    setLoadingData(true);
    try {
      const result = await getOrgSettingsCallable({});
      const { settings: orgSettings } = result.data;

      setSettings(orgSettings);
      setTagline(orgSettings.tagline || '');

      // Load communication settings if exists
      if (orgSettings.communicationSettings) {
        setCommSettings(prev => ({
          ...prev,
          ...orgSettings.communicationSettings
        }));
      }

      // Load logo preview if exists
      if (orgSettings.logo) {
        try {
          const logoRef = ref(storage, orgSettings.logo);
          const logoUrl = await getDownloadURL(logoRef);
          setLogoPreview(logoUrl);
        } catch (logoError) {
          console.error('Error loading logo preview:', logoError);
        }
      }
    } catch (error) {
      console.error('Error fetching organization settings:', error);
      setMessage({
        type: 'danger',
        text: error.message || 'Failed to load organization settings.'
      });
    } finally {
      setLoadingData(false);
    }
  }, [userOrgId]);

  useEffect(() => {
    if (!loadingAuth && userOrgId && isAdmin) {
      fetchSettings();
    } else if (!loadingAuth) {
      setLoadingData(false);
    }
  }, [loadingAuth, userOrgId, isAdmin, fetchSettings]);

  // Fetch organization details (name, address, timezone)
  useEffect(() => {
    if (!userOrgId || !isAdmin) return;
    const fetchOrgData = async () => {
      try {
        const orgDocSnap = await getDoc(doc(db, 'organization', userOrgId));
        if (orgDocSnap.exists()) {
          const data = orgDocSnap.data();
          setOrgData(data);
          setOrgEditData({
            orgName: data.orgName || '',
            orgAddress: data.orgAddress || '',
            orgCity: data.orgCity || '',
            orgState: data.orgState || '',
            orgZip: data.orgZip || ''
          });
        }
      } catch (err) {
        console.error('Error fetching organization data:', err);
      }
    };
    fetchOrgData();
  }, [userOrgId, isAdmin]);

  // Org detail edit handlers
  const handleStartEditOrg = () => {
    setOrgEditData({
      orgName: orgData?.orgName || '',
      orgAddress: orgData?.orgAddress || '',
      orgCity: orgData?.orgCity || '',
      orgState: orgData?.orgState || '',
      orgZip: orgData?.orgZip || ''
    });
    setEditingOrg(true);
  };

  const handleCancelEditOrg = () => {
    setEditingOrg(false);
  };

  const handleSaveOrg = async () => {
    if (!userOrgId) return;
    if (!orgEditData.orgName.trim()) {
      setMessage({ type: 'danger', text: 'Organization name is required.' });
      return;
    }
    setSavingOrg(true);
    setMessage({ type: '', text: '' });
    try {
      const orgDocRef = doc(db, 'organization', userOrgId);
      const updateData = {
        orgName: orgEditData.orgName.trim(),
        orgAddress: orgEditData.orgAddress.trim(),
        orgCity: orgEditData.orgCity.trim(),
        orgState: orgEditData.orgState,
        orgZip: orgEditData.orgZip.trim()
      };
      await updateDoc(orgDocRef, updateData);
      setOrgData(prev => ({ ...prev, ...updateData }));
      setEditingOrg(false);
      setMessage({ type: 'success', text: 'Organization details saved!' });
    } catch (err) {
      console.error('Error updating organization:', err);
      setMessage({ type: 'danger', text: 'Failed to update organization details.' });
    } finally {
      setSavingOrg(false);
    }
  };

  const handleTimezoneChange = async (e) => {
    const newTimezone = e.target.value;
    if (!userOrgId) return;
    setSavingTimezone(true);
    setTimezoneSuccess(false);
    try {
      await updateDoc(doc(db, 'organization', userOrgId), { timezone: newTimezone });
      setOrgData(prev => ({ ...prev, timezone: newTimezone }));
      setTimezoneSuccess(true);
      setTimeout(() => setTimezoneSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating timezone:', err);
      setMessage({ type: 'danger', text: 'Failed to update timezone.' });
    } finally {
      setSavingTimezone(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setMessage({
        type: 'danger',
        text: `Invalid file type. Please select a PNG, JPEG, or WebP image.`
      });
      return;
    }

    // Validate file size
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setMessage({
        type: 'danger',
        text: `File is too large. Maximum size is ${MAX_LOGO_SIZE_MB}MB.`
      });
      return;
    }

    setLogoFile(file);
    setMessage({ type: '', text: '' });

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle logo upload
  const handleUploadLogo = async () => {
    if (!logoFile) {
      setMessage({ type: 'warning', text: 'Please select a logo image first.' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(logoFile);
      });

      const base64Data = await base64Promise;

      // Upload via Cloud Function
      const result = await uploadOrgLogoCallable({
        logoBase64: base64Data,
        mimeType: logoFile.type
      });

      if (result.data.success) {
        setMessage({ type: 'success', text: 'Logo uploaded successfully!' });
        setLogoFile(null);
        setSettings(prev => ({ ...prev, logo: result.data.logoPath }));
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({
        type: 'danger',
        text: error.message || 'Failed to upload logo.'
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle logo deletion
  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the organization logo?')) {
      return;
    }

    setDeleting(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await deleteOrgLogoCallable({});

      if (result.data.success) {
        setMessage({ type: 'success', text: 'Logo deleted successfully.' });
        setLogoPreview(null);
        setLogoFile(null);
        setSettings(prev => ({ ...prev, logo: null }));
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error('Error deleting logo:', error);
      setMessage({
        type: 'danger',
        text: error.message || 'Failed to delete logo.'
      });
    } finally {
      setDeleting(false);
    }
  };

  // Handle tagline save
  const handleSaveTagline = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await updateOrgSettingsCallable({ tagline });

      if (result.data.success) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        setSettings(prev => ({ ...prev, tagline }));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({
        type: 'danger',
        text: error.message || 'Failed to save settings.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle communication settings save
  const handleSaveCommSettings = async () => {
    setSavingComm(true);
    setMessage({ type: '', text: '' });

    // Validate dailyCapPerMember is within range
    const cap = commSettings.dailyCapPerMember;
    if (!Number.isInteger(cap) || cap < 1 || cap > 10) {
      setMessage({ type: 'danger', text: 'Daily message cap must be a whole number between 1 and 10.' });
      setSavingComm(false);
      return;
    }

    // Validate quiet hours are set if either is provided
    if ((commSettings.quietHoursStart && !commSettings.quietHoursEnd) ||
        (!commSettings.quietHoursStart && commSettings.quietHoursEnd)) {
      setMessage({ type: 'danger', text: 'Both quiet hours start and end must be set, or leave both empty.' });
      setSavingComm(false);
      return;
    }

    try {
      await updateOrgSettingsCallable({ communicationSettings: commSettings });
      setMessage({ type: 'success', text: 'Communication settings saved!' });
    } catch (error) {
      console.error('Error saving communication settings:', error);
      setMessage({
        type: 'danger',
        text: error.message || 'Failed to save communication settings.'
      });
    } finally {
      setSavingComm(false);
    }
  };

  // Trigger file input click
  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  // Loading state
  if (loadingAuth) {
    return (
      <div className="org-settings-page">
        <div className="org-settings-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="org-settings-page">
      {/* Header */}
      <div className="org-settings-header">
        <div className="container">
          <Link to="/dashboard" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back to Dashboard</span>
          </Link>
          <h1 className="org-settings-title">
            <FontAwesomeIcon icon={faBuilding} className="title-icon" />
            Organization Settings <span className="admin-only-badge">Admin Only</span>
          </h1>
          <p className="org-settings-subtitle">
            Manage your organization's branding and directory settings
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="org-settings-content">
        <div className="container">
          {/* Messages */}
          {message.text && (
            <Alert
              variant={message.type}
              onClose={() => setMessage({ type: '', text: '' })}
              dismissible
              className="org-settings-alert"
            >
              <FontAwesomeIcon
                icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle}
                className="me-2"
              />
              {message.text}
            </Alert>
          )}

          {loadingData ? (
            <div className="org-settings-loading-card">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Loading settings...</p>
            </div>
          ) : (
            <div className="org-settings-grid">
              {/* Organization Details Section */}
              <div className="org-settings-card">
                <div className="card-header">
                  <FontAwesomeIcon icon={faBuilding} />
                  <h2>Organization Details</h2>
                  {!editingOrg && (
                    <button
                      className="btn-edit-org"
                      onClick={handleStartEditOrg}
                      title="Edit organization details"
                      style={{ marginLeft: 'auto', background: 'none', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0.35rem 0.65rem', color: '#64748b', cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                    >
                      <FontAwesomeIcon icon={faPencilAlt} /> Edit
                    </button>
                  )}
                </div>
                <div className="card-body">
                  {editingOrg ? (
                    <div>
                      <div className="form-group mb-3">
                        <label htmlFor="orgName">Organization Name</label>
                        <input
                          type="text"
                          id="orgName"
                          className="form-control"
                          value={orgEditData.orgName}
                          onChange={(e) => setOrgEditData(prev => ({ ...prev, orgName: e.target.value }))}
                          disabled={savingOrg}
                          placeholder="Organization name"
                        />
                      </div>
                      <div className="form-group mb-3">
                        <label htmlFor="orgAddress">Street Address</label>
                        <input
                          type="text"
                          id="orgAddress"
                          className="form-control"
                          value={orgEditData.orgAddress}
                          onChange={(e) => setOrgEditData(prev => ({ ...prev, orgAddress: e.target.value }))}
                          disabled={savingOrg}
                          placeholder="123 Main Street"
                        />
                      </div>
                      <div className="row">
                        <div className="col-md-5">
                          <div className="form-group mb-3">
                            <label htmlFor="orgCity">City</label>
                            <input
                              type="text"
                              id="orgCity"
                              className="form-control"
                              value={orgEditData.orgCity}
                              onChange={(e) => setOrgEditData(prev => ({ ...prev, orgCity: e.target.value }))}
                              disabled={savingOrg}
                              placeholder="City"
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group mb-3">
                            <label htmlFor="orgState">State</label>
                            <select
                              id="orgState"
                              className="form-control"
                              value={orgEditData.orgState}
                              onChange={(e) => setOrgEditData(prev => ({ ...prev, orgState: e.target.value }))}
                              disabled={savingOrg}
                            >
                              <option value="">Select...</option>
                              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-3">
                          <div className="form-group mb-3">
                            <label htmlFor="orgZip">Zip</label>
                            <input
                              type="text"
                              id="orgZip"
                              className="form-control"
                              value={orgEditData.orgZip}
                              onChange={(e) => setOrgEditData(prev => ({ ...prev, orgZip: e.target.value }))}
                              disabled={savingOrg}
                              placeholder="12345"
                              maxLength="10"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-group mb-3">
                        <label htmlFor="orgTimezone">Timezone</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <select
                            id="orgTimezone"
                            className="form-control"
                            style={{ maxWidth: '300px' }}
                            value={orgData?.timezone || ''}
                            onChange={handleTimezoneChange}
                            disabled={savingTimezone}
                          >
                            <option value="">Select timezone...</option>
                            {TIMEZONES.map(tz => (
                              <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                          </select>
                          {savingTimezone && <FontAwesomeIcon icon={faSpinner} spin style={{ color: '#64748b' }} />}
                          {timezoneSuccess && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#16a34a' }} />}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                        <button
                          className="btn btn-primary"
                          onClick={handleSaveOrg}
                          disabled={savingOrg}
                        >
                          {savingOrg ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                          ) : (
                            <><FontAwesomeIcon icon={faSave} /> Save Changes</>
                          )}
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={handleCancelEditOrg}
                          disabled={savingOrg}
                        >
                          <FontAwesomeIcon icon={faTimes} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="org-detail-rows">
                      {orgData?.orgName && (
                        <div className="org-detail-row">
                          <span className="org-detail-label">Name</span>
                          <span className="org-detail-value">{orgData.orgName}</span>
                        </div>
                      )}
                      {(orgData?.orgAddress || orgData?.orgCity) && (
                        <div className="org-detail-row">
                          <span className="org-detail-label">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" /> Address
                          </span>
                          <span className="org-detail-value">
                            {[orgData?.orgAddress, [orgData?.orgCity, orgData?.orgState, orgData?.orgZip].filter(Boolean).join(', ')].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                      <div className="org-detail-row">
                        <span className="org-detail-label">
                          <FontAwesomeIcon icon={faClock} className="me-1" /> Timezone
                        </span>
                        <span className="org-detail-value">
                          {TIMEZONES.find(tz => tz.value === orgData?.timezone)?.label || orgData?.timezone || 'Not set'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Logo Section */}
              <div className="org-settings-card">
                <div className="card-header">
                  <FontAwesomeIcon icon={faImage} />
                  <h2>Organization Logo</h2>
                </div>
                <div className="card-body">
                  <p className="card-description">
                    Upload your organization's logo to display on the directory cover page.
                    Recommended size: 400x400 pixels. Max file size: {MAX_LOGO_SIZE_MB}MB.
                  </p>

                  {/* Logo Preview */}
                  <div className="logo-preview-container">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Organization logo preview"
                        className="logo-preview"
                      />
                    ) : (
                      <div className="logo-placeholder">
                        <FontAwesomeIcon icon={faImage} />
                        <span>No logo uploaded</span>
                      </div>
                    )}
                  </div>

                  {/* File Input (hidden) */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />

                  {/* Logo Actions */}
                  <div className="logo-actions">
                    <button
                      type="button"
                      className="btn-select-file"
                      onClick={handleSelectFile}
                      disabled={uploading || deleting}
                    >
                      <FontAwesomeIcon icon={faImage} />
                      {logoFile ? 'Change Selection' : 'Select Image'}
                    </button>

                    {logoFile && (
                      <button
                        type="button"
                        className="btn-upload"
                        onClick={handleUploadLogo}
                        disabled={uploading || deleting}
                      >
                        {uploading ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faUpload} />
                            Upload Logo
                          </>
                        )}
                      </button>
                    )}

                    {settings.logo && !logoFile && (
                      <button
                        type="button"
                        className="btn-delete"
                        onClick={handleDeleteLogo}
                        disabled={uploading || deleting}
                      >
                        {deleting ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faTrashAlt} />
                            Remove Logo
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {logoFile && (
                    <p className="selected-file-info">
                      Selected: {logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>
              </div>

              {/* Tagline Section */}
              <div className="org-settings-card">
                <div className="card-header">
                  <FontAwesomeIcon icon={faBuilding} />
                  <h2>Directory Tagline</h2>
                </div>
                <div className="card-body">
                  <p className="card-description">
                    Add an optional tagline or verse that appears on the directory cover page.
                  </p>

                  <div className="org-info-display">
                    <label>Organization Name</label>
                    <p className="org-name-display">{settings.name}</p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="tagline">Tagline / Verse</label>
                    <textarea
                      id="tagline"
                      className="tagline-input"
                      value={tagline}
                      onChange={(e) => setTagline(e.target.value)}
                      placeholder='e.g., "For where two or three gather in my name, there am I with them." - Matthew 18:20'
                      maxLength={200}
                      rows={3}
                    />
                    <div className="char-count">
                      {tagline.length}/200 characters
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-save"
                    onClick={handleSaveTagline}
                    disabled={saving || tagline === (settings.tagline || '')}
                  >
                    {saving ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} />
                        Save Tagline
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Communication Settings Section */}
              <div className="org-settings-card">
                <div className="card-header">
                  <FontAwesomeIcon icon={faBell} />
                  <h2>Communication Settings</h2>
                </div>
                <div className="card-body">
                  <p className="card-description">
                    Configure how automated messages and announcements are delivered to members.
                  </p>

                  <div className="form-group mb-3">
                    <label htmlFor="dailyCap">Daily Message Cap per Member</label>
                    <input
                      type="number"
                      id="dailyCap"
                      className="form-control"
                      min="1"
                      max="10"
                      value={commSettings.dailyCapPerMember}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setCommSettings(prev => ({
                          ...prev,
                          dailyCapPerMember: Number.isNaN(val) ? 3 : Math.min(10, Math.max(1, val))
                        }));
                      }}
                    />
                    <small className="text-muted">Maximum messages a member can receive per day (1-10)</small>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label htmlFor="quietStart">Quiet Hours Start</label>
                        <input
                          type="time"
                          id="quietStart"
                          className="form-control"
                          value={commSettings.quietHoursStart}
                          onChange={(e) => setCommSettings(prev => ({
                            ...prev,
                            quietHoursStart: e.target.value
                          }))}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label htmlFor="quietEnd">Quiet Hours End</label>
                        <input
                          type="time"
                          id="quietEnd"
                          className="form-control"
                          value={commSettings.quietHoursEnd}
                          onChange={(e) => setCommSettings(prev => ({
                            ...prev,
                            quietHoursEnd: e.target.value
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                  <small className="text-muted d-block mb-3">
                    Messages won't be sent during quiet hours (typically overnight).
                  </small>

                  <button
                    className="btn btn-primary"
                    onClick={handleSaveCommSettings}
                    disabled={savingComm}
                  >
                    {savingComm ? (
                      <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                    ) : (
                      <><FontAwesomeIcon icon={faSave} /> Save Communication Settings</>
                    )}
                  </button>
                </div>
              </div>

              {/* Preview Info */}
              <div className="org-settings-card org-settings-info">
                <div className="card-header">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <h2>Directory Preview</h2>
                </div>
                <div className="card-body">
                  <p className="card-description">
                    Your organization branding will appear on the PDF directory cover page.
                    Generate a new directory from the Families page to see your changes.
                  </p>
                  <Link to="/families" className="btn-families-link">
                    Go to Families Page
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrganizationSettingsPage;
